import {loadStripe} from '@stripe/stripe-js';
import {getSetting} from '@wc-stripe/utils';

let stripeSDKPromise = null;
let cachedPublicKey = null;
let cachedSdkParams = null;

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Loads Stripe.js, retrying a few times if the script fails to load (e.g. a transient
 * network/CDN hiccup) rather than giving up after the first attempt.
 *
 * @param {string} publicKey
 * @param {Object} sdkParams
 * @returns {Promise<Stripe>}
 */
const loadStripeWithRetry = async (publicKey, sdkParams) => {
    let lastError;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            const stripe = await loadStripe(publicKey, sdkParams);
            if (!stripe) {
                throw new Error('Stripe.js failed to load.');
            }
            return stripe;
        } catch (error) {
            lastError = error;
            if (attempt < MAX_ATTEMPTS) {
                await delay(RETRY_DELAY_MS);
            }
        }
    }
    throw lastError;
};

/**
 * Load Stripe SDK
 *
 * Returns cached Stripe instance if public_key and sdkParams haven't changed.
 * Reloads SDK if parameters change (e.g., switching between test/live mode).
 * A failed load is never cached - the next call retries from scratch instead of
 * being stuck with a permanently broken SDK for the rest of the page's lifetime.
 *
 * @returns {Promise<Stripe>} Stripe instance
 */
const loadStripeSDK = async () => {
    const publicKey = getSetting('publicKey');
    const sdkParams = getSetting('sdkParams');

    // Check if SDK needs to be reloaded due to parameter changes
    const paramsChanged = cachedPublicKey !== publicKey ||
        JSON.stringify(cachedSdkParams) !== JSON.stringify(sdkParams);

    if (!stripeSDKPromise || paramsChanged) {
        // Store current parameters
        cachedPublicKey = publicKey;
        cachedSdkParams = sdkParams;

        // Load Stripe SDK with current parameters
        stripeSDKPromise = loadStripeWithRetry(publicKey, sdkParams).catch((error) => {
            // Don't cache a failed attempt - let the next call retry from scratch.
            stripeSDKPromise = null;
            throw error;
        });
    }

    return stripeSDKPromise;
}

export {
    loadStripeSDK
}